import PrintIcon from "@mui/icons-material/Print";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {LoadingButton} from "@mui/lab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {ResourceDisplayKind} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import type {Organization} from "crqlar-react-common/dist/models/organization/organization";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import type {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {SectorType, type Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import type {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {ResourceCategoryService} from "crqlar-react-common/dist/services/resourceCategory/resourceCategory.service";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {
    convertLocalDateToDate,
    endOfDay,
    formatDateDMYHM,
    formatFullDate,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {usePrint} from "crqlar-react-common/dist/utils/hooks/print/usePrint";
import {enqueueSnackbar} from "notistack";
import {useCallback, useEffect, useMemo, useState, type MouseEvent} from "react";
import {useTranslation} from "react-i18next";
import DefaultFallbackLogo from "../../assets/media/images/crqlar-transparent-logo-white.png";
import {
    clearServerTablePrintConfig,
    hasPrintColumnBelowMinimumWidth,
    MIN_PRINT_COLUMN_WIDTH,
    PRINT_PAGE_SIZES,
    PRINT_TEXT_SIZES,
    PrintPreviewScaler,
    readServerTablePrintConfig,
    ServerTablePrintColumnPicker,
    ServerTablePrintColumnRibbon,
    type ServerTablePrintColumnLayout,
    type ServerTablePrintOrientation,
    type ServerTablePrintRenderMode,
    type ServerTablePrintTextSize,
    writeServerTablePrintConfig,
} from "../common/serverTable";
import {SimpleChipElement} from "../common/uiElements/SimpleChipElement";
import {getResourceDisplayKind} from "../common/uiElements/utils";

interface LegacyAgendatPrintButtonProps {
    profileId?: string;
    fallbackStartDate?: Date;
    fallbackEndDate?: Date;
    sectors: Sector[];
    organization?: Organization;
}

interface AgendaRow {
    reservationId: string;
    serviceName: string;
    resourceNames: string[];
    start?: Date;
    end?: Date;
    sectorName: string;
}

interface AgendaRows {
    weekly: AgendaRow[];
    spa: AgendaRow[];
    restaurant: AgendaRow[];
    other: AgendaRow[];
}

type AgendaPrintColumnId = "name" | "start" | "end" | "sector" | "resources";

interface AgendaPrintColumn extends ServerTablePrintColumnLayout {
    id: AgendaPrintColumnId;
}

const EMPTY_AGENDA_ROWS: AgendaRows = {
    weekly: [],
    spa: [],
    restaurant: [],
    other: [],
};

const A4_PAGE_PADDING = 38;
const LEGACY_AGENDA_PRINT_CONFIG_ID = "legacy-agenda";
const DEFAULT_AGENDA_PRINT_COLUMN_IDS: AgendaPrintColumnId[] = [
    "name",
    "start",
    "end",
    "sector",
    "resources",
];

const getAgendaPrintCss = (
    orientation: ServerTablePrintOrientation,
    fontSize: number,
) => `
  @page { size: A4 ${orientation}; margin: 10mm; }
  @media print {
    .legacy-agenda-print-root {
      width: ${orientation === "portrait" ? 190 : 277}mm !important;
      max-width: 100% !important;
      margin: 0 auto !important;
    }
    .legacy-agenda-print-root,
    .legacy-agenda-print-root div,
    .legacy-agenda-print-root span,
    .legacy-agenda-print-root .MuiTypography-root {
      font-size: ${fontSize}px !important;
    }
    .legacy-agenda-section-title { font-size: ${fontSize + 2}px !important; }
    .legacy-agenda-last-name { font-size: ${fontSize + 8}px !important; }
    .legacy-agenda-first-name { font-size: ${fontSize + 4}px !important; }
    .legacy-agenda-empty { font-size: ${fontSize + 12}px !important; }
    .legacy-agenda-grid-row {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
  }
`;

const LegacyAgendatPrintButton = ({
    profileId,
    fallbackStartDate,
    fallbackEndDate,
    sectors,
    organization,
}: LegacyAgendatPrintButtonProps) => {
    const {t, i18n} = useTranslation();
    const fullScreen = useMediaQuery("(max-width:900px)");
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [profile, setProfile] = useState<Profile>();
    const [printStartDate, setPrintStartDate] = useState<Date>();
    const [printEndDate, setPrintEndDate] = useState<Date>();
    const [agendaRows, setAgendaRows] = useState<AgendaRows>(EMPTY_AGENDA_ROWS);
    const [orientation, setOrientation] = useState<ServerTablePrintOrientation>("portrait");
    const [textSize, setTextSize] = useState<ServerTablePrintTextSize>("standard");
    const [renderMode, setRenderMode] = useState<ServerTablePrintRenderMode>("raw");
    const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>(DEFAULT_AGENDA_PRINT_COLUMN_IDS);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const fontSize = PRINT_TEXT_SIZES[textSize];
    const printCss = useMemo(
        () => getAgendaPrintCss(orientation, fontSize),
        [fontSize, orientation],
    );
    const {handlePrint, printRef} = usePrint({
        documentTitle: "Agenda",
        css: printCss,
    });

    const sectorsById = useMemo(
        () => new Map(
            sectors
                .filter(sector => Boolean(sector.id))
                .map(sector => [sector.id!, sector]),
        ),
        [sectors],
    );
    const collator = useMemo(
        () => new Intl.Collator(i18n.resolvedLanguage || i18n.language, {
            sensitivity: "base",
            numeric: true,
        }),
        [i18n.language, i18n.resolvedLanguage],
    );
    const printColumns = useMemo<AgendaPrintColumn[]>(() => [
        {id: "name", label: t("Name"), width: 200},
        {id: "start", label: t("Start"), width: 155},
        {id: "end", label: t("End"), width: 155},
        {id: "sector", label: t("Sector"), width: 150},
        {id: "resources", label: t("Resources"), width: 220},
    ], [t]);
    const selectedColumns = useMemo(() => {
        const columnsById = new Map(printColumns.map(column => [column.id, column]));
        return selectedColumnIds
            .map(id => columnsById.get(id as AgendaPrintColumnId))
            .filter((column): column is AgendaPrintColumn => Boolean(column))
            .map(column => ({
                ...column,
                width: columnWidths[column.id] ?? column.width,
            }));
    }, [columnWidths, printColumns, selectedColumnIds]);
    const pageSize = PRINT_PAGE_SIZES[orientation];
    const printContentWidth = pageSize.width - 2 * A4_PAGE_PADDING;
    const hasWidthWarning = hasPrintColumnBelowMinimumWidth(
        selectedColumns,
        printContentWidth,
    );
    const canPrint = selectedColumns.length > 0;

    useEffect(() => {
        if (!isPreviewOpen) return;

        const persisted = readServerTablePrintConfig(LEGACY_AGENDA_PRINT_CONFIG_ID);
        const availableIds = new Set(printColumns.map(column => column.id));
        const storedIds = persisted?.columnIds.filter(id => availableIds.has(id as AgendaPrintColumnId)) ?? [];

        setOrientation(persisted?.orientation ?? "portrait");
        setTextSize(persisted?.textSize ?? "standard");
        setRenderMode(persisted?.renderMode ?? "raw");
        setSelectedColumnIds(storedIds.length ? storedIds : DEFAULT_AGENDA_PRINT_COLUMN_IDS);
        setColumnWidths(persisted?.columnWidths ?? {});
    }, [isPreviewOpen, printColumns]);

    const buildRows = useCallback((
        reservations: Reservation[],
        servicesById: Record<string, Service>,
        resourcesById: Record<string, Resource>,
        resourceCategoriesById: ReadonlyMap<string, ResourceCategory>,
        sectorList: Sector[],
    ): AgendaRow[] => {
        const sectorIds = new Set(sectorList.map(sector => sector.id));
        const getResourcePriority = (
            resource: Resource,
            sectorType: SectorType | undefined,
        ) => {
            if (sectorType === SectorType.RESTAURANT) {
                return resource.parentId ? 1 : 0;
            }
            if (sectorType === SectorType.SPA) {
                return getResourceDisplayKind(
                    resource,
                    sectorsById,
                    resourceCategoriesById,
                ) === ResourceDisplayKind.STAFF ? 0 : 1;
            }
            return 0;
        };

        return reservations
            .filter(reservation => reservation.sectorId && sectorIds.has(reservation.sectorId))
            .map(reservation => {
                const sector = sectorsById.get(reservation.sectorId!);
                const resourceNames = Array.from(new Set((reservation.resourceIds ?? [])
                    .map(resourceId => resourcesById[resourceId])
                    .filter((resource): resource is Resource => Boolean(resource))
                    .sort((left, right) => (
                        getResourcePriority(left, sector?.sectorType)
                        - getResourcePriority(right, sector?.sectorType)
                        || collator.compare(left.name ?? "", right.name ?? "")
                        || collator.compare(left.id ?? "", right.id ?? "")
                    ))
                    .map(resource => resource.name)
                    .filter((name): name is string => Boolean(name))));

                return {
                    reservationId: reservation.id!,
                    serviceName: reservation.serviceId
                        ? servicesById[reservation.serviceId]?.name ?? "-"
                        : "-",
                    resourceNames,
                    start: reservation.expectedStartDate ?? new Date(),
                    end: reservation.expectedEndDate ?? new Date(),
                    sectorName: sector?.name ?? "-",
                };
            })
            .sort((left, right) => (
                Number(left.serviceName === "-") - Number(right.serviceName === "-")
                || collator.compare(left.serviceName, right.serviceName)
                || (left.start?.getTime() ?? 0) - (right.start?.getTime() ?? 0)
                || collator.compare(left.reservationId, right.reservationId)
            ));
    }, [collator, sectorsById]);

    const openPrintPreview = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!profileId || isLoading) return;

        setIsLoading(true);
        setIsPreviewOpen(false);
        setProfile(undefined);
        setAgendaRows(EMPTY_AGENDA_ROWS);

        try {
            const nextProfile = await ProfileService.getProfileById(profileId);
            const activeStay = nextProfile.currentStay ?? nextProfile.activeStays?.[0];
            const startDate = convertLocalDateToDate(activeStay?.startDay)
                ?? fallbackStartDate
                ?? new Date();
            const endDate = convertLocalDateToDate(activeStay?.endDay)
                ?? fallbackEndDate
                ?? startDate;
            const reservationPage = await ReservationService.getAllReservations({
                profileIds: [profileId],
                overlaps: {
                    start: startOfDay(startDate),
                    end: endOfDay(endDate),
                },
                limit: {offset: 0, limit: 300},
            });
            const reservations = reservationPage.content ?? [];
            const serviceIds = Array.from(new Set(
                reservations
                    .map(reservation => reservation.serviceId)
                    .filter((serviceId): serviceId is string => Boolean(serviceId)),
            ));
            const resourceIds = Array.from(new Set(
                reservations.flatMap(reservation => reservation.resourceIds ?? []),
            ));
            const [services, resources, resourceCategories] = await Promise.all([
                serviceIds.length
                    ? ServiceService.getAllServices({
                        id: serviceIds,
                        limit: {offset: 0, limit: 300},
                    }).then(page => page.content ?? [])
                    : Promise.resolve([] as Service[]),
                resourceIds.length
                    ? ResourceService.getAllResources({
                        id: resourceIds,
                        limit: {offset: 0, limit: 1000},
                    }).then(page => page.content ?? [])
                    : Promise.resolve([] as Resource[]),
                resourceIds.length
                    ? ResourceCategoryService.getAllResourceCategories({
                        limit: {
                            offset: 0,
                            limit: 1000,
                            sort: [{field: "name", direction: "ASC"}],
                        },
                    }).then(page => page.content ?? [])
                    : Promise.resolve([] as ResourceCategory[]),
            ]);
            const servicesById = Object.fromEntries(
                services.map(service => [service.id, service]),
            );
            const resourcesById = Object.fromEntries(
                resources.map(resource => [resource.id, resource]),
            );
            const resourceCategoriesById = new Map(
                resourceCategories
                    .filter(category => Boolean(category.id))
                    .map(category => [category.id!, category]),
            );

            setProfile(nextProfile);
            setPrintStartDate(startDate);
            setPrintEndDate(endDate);
            setAgendaRows({
                weekly: buildRows(
                    reservations,
                    servicesById,
                    resourcesById,
                    resourceCategoriesById,
                    sectors.filter(sector => sector.sectorType === SectorType.WEEKLY_PLANNER),
                ),
                spa: buildRows(
                    reservations,
                    servicesById,
                    resourcesById,
                    resourceCategoriesById,
                    sectors.filter(sector => sector.sectorType === SectorType.SPA),
                ),
                restaurant: buildRows(
                    reservations,
                    servicesById,
                    resourcesById,
                    resourceCategoriesById,
                    sectors.filter(sector => sector.sectorType === SectorType.RESTAURANT),
                ),
                other: buildRows(
                    reservations,
                    servicesById,
                    resourcesById,
                    resourceCategoriesById,
                    sectors.filter(sector => sector.sectorType === SectorType.OTHER),
                ),
            });
            setIsPreviewOpen(true);
            setIsLoading(false);
        } catch (error) {
            console.error("Legacy agenda preview failed", error);
            enqueueSnackbar(t("Failed to print agenda"), {variant: "error"});
            setIsLoading(false);
        }
    }, [
        buildRows,
        fallbackEndDate,
        fallbackStartDate,
        isLoading,
        profileId,
        sectors,
        t,
    ]);

    const handleSelectionChange = useCallback((ids: string[]) => {
        setSelectedColumnIds(current => {
            const kept = current.filter(id => ids.includes(id));
            const added = ids.filter(id => !current.includes(id));
            return [...kept, ...added];
        });
    }, []);

    const resetPrintConfig = useCallback(() => {
        clearServerTablePrintConfig(LEGACY_AGENDA_PRINT_CONFIG_ID);
        setOrientation("portrait");
        setTextSize("standard");
        setRenderMode("raw");
        setSelectedColumnIds(DEFAULT_AGENDA_PRINT_COLUMN_IDS);
        setColumnWidths({});
    }, []);

    const confirmPrint = useCallback(async () => {
        if (!canPrint) return;

        writeServerTablePrintConfig(LEGACY_AGENDA_PRINT_CONFIG_ID, {
            orientation,
            textSize,
            renderMode,
            columnIds: selectedColumnIds,
            columnWidths,
        });
        await handlePrint();
        setIsPreviewOpen(false);
    }, [
        canPrint,
        columnWidths,
        handlePrint,
        orientation,
        renderMode,
        selectedColumnIds,
        textSize,
    ]);

    const renderGridTable = (title: string, rows: AgendaRow[]) => {
        if (!rows.length) return null;

        const gridTemplateColumns = selectedColumns
            .map(column => `${column.width}fr`)
            .join(" ");
        const renderCell = (columnId: AgendaPrintColumnId, row: AgendaRow) => {
            switch (columnId) {
                case "name":
                    return row.serviceName;
                case "start":
                    return formatDateDMYHM(row.start);
                case "end":
                    return formatDateDMYHM(row.end);
                case "sector":
                    return renderMode === "original" && row.sectorName !== "-"
                        ? <SimpleChipElement values={[row.sectorName]}/>
                        : row.sectorName;
                case "resources":
                    if (!row.resourceNames.length) return "-";
                    return renderMode === "original"
                        ? <SimpleChipElement values={row.resourceNames}/>
                        : row.resourceNames.join(", ");
            }
        };

        return (
            <div style={{marginTop: 24}}>
                <Typography
                    className="legacy-agenda-section-title"
                    sx={{fontSize: fontSize + 2, fontWeight: 700}}
                    gutterBottom
                >
                    {title}
                </Typography>

                <div
                    className="legacy-agenda-grid-row"
                    style={{
                        display: "grid",
                        gridTemplateColumns,
                        padding: "10px 12px",
                        borderBottom: `1px solid ${CommonThemeColors.ref.neutral.neutral80}`,
                        backgroundColor: renderMode === "original" ? "#f6f8fb" : "transparent",
                    }}
                >
                    {selectedColumns.map(column => (
                        <div
                            key={column.id}
                            style={{
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize,
                                fontWeight: 600,
                            }}
                        >
                            {column.label}
                        </div>
                    ))}
                </div>

                {rows.map((row, index) => (
                    <div
                        key={row.reservationId}
                        className="legacy-agenda-grid-row"
                        style={{
                            display: "grid",
                            gridTemplateColumns,
                            padding: "8px 12px",
                            borderBottom: index === rows.length - 1 ? "none" : "1px solid #f0f0f0",
                            fontSize,
                        }}
                    >
                        {selectedColumns.map(column => (
                            <div
                                key={column.id}
                                style={{
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {renderCell(column.id, row)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const activeStay = profile?.currentStay ?? profile?.activeStays?.[0];
    const lastName = [profile?.academicTitle, profile?.lastName].filter(Boolean).join(" ").trim();
    const firstAndPrefix = [profile?.namePrefix, profile?.firstName].filter(Boolean).join(" ").trim();
    const headerLogo = organization?.media?.mediaURI
        ?? organization?.logo?.mediaURI
        ?? DefaultFallbackLogo;
    const hasAgenda = Object.values(agendaRows).some(rows => rows.length > 0);

    return (
        <>
            <LoadingButton
                data-no-row-click
                type="button"
                size="small"
                loading={isLoading}
                loadingPosition="start"
                startIcon={<PrintIcon/>}
                disabled={!profileId || !sectors.length}
                onClick={openPrintPreview}
            >
                {t("Print (legacy)")}
            </LoadingButton>

            {profile && (
                <Dialog
                    open={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    onClick={event => event.stopPropagation()}
                    maxWidth="xl"
                    fullWidth
                    fullScreen={fullScreen}
                >
                    <DialogTitle sx={{px: {xs: 2, sm: 3}, pt: {xs: 2, sm: 2.5}, pb: 1}}>
                        {t("Print Preview")}
                    </DialogTitle>

                    <DialogContent sx={{px: {xs: 1.5, sm: 3}, pb: 2}}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", lg: "360px minmax(0, 1fr)"},
                                gap: 2,
                                alignItems: "stretch",
                            }}
                        >
                            <Stack sx={{pt: 1, gap: 2, minHeight: {lg: "68vh"}, minWidth: 0}}>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                        gap: 1.5,
                                    }}
                                >
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>{t("Orientation")}</InputLabel>
                                        <Select
                                            label={t("Orientation")}
                                            value={orientation}
                                            onChange={event => setOrientation(
                                                event.target.value as ServerTablePrintOrientation,
                                            )}
                                        >
                                            <MenuItem value="landscape">{t("A4 Landscape")}</MenuItem>
                                            <MenuItem value="portrait">{t("A4 Portrait")}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" fullWidth>
                                        <InputLabel>{t("Text size")}</InputLabel>
                                        <Select
                                            label={t("Text size")}
                                            value={textSize}
                                            onChange={event => setTextSize(
                                                event.target.value as ServerTablePrintTextSize,
                                            )}
                                        >
                                            <MenuItem value="compact">{t("Compact")}</MenuItem>
                                            <MenuItem value="standard">{t("Standard")}</MenuItem>
                                            <MenuItem value="large">{t("Large")}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" fullWidth>
                                        <InputLabel>{t("Print style")}</InputLabel>
                                        <Select
                                            label={t("Print style")}
                                            value={renderMode}
                                            onChange={event => setRenderMode(
                                                event.target.value as ServerTablePrintRenderMode,
                                            )}
                                        >
                                            <MenuItem value="raw">{t("Raw")}</MenuItem>
                                            <MenuItem value="original">{t("Original")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <ServerTablePrintColumnPicker
                                    columns={printColumns}
                                    selectedColumnIds={selectedColumnIds}
                                    onChange={handleSelectionChange}
                                />

                                {hasWidthWarning && (
                                    <Alert severity="warning" variant="outlined">
                                        {t("The selected columns may be too wide for the current paper setup. Use landscape or fewer columns for a more readable printout.")}
                                    </Alert>
                                )}

                                {!canPrint && (
                                    <Alert severity="warning" variant="outlined">
                                        {t("Select at least one print column.")}
                                    </Alert>
                                )}
                            </Stack>

                            <Stack sx={{gap: 1, minWidth: 0}}>
                                {selectedColumns.length > 0 && (
                                    <Box>
                                        <ServerTablePrintColumnRibbon
                                            columns={selectedColumns}
                                            minWidthFraction={MIN_PRINT_COLUMN_WIDTH / printContentWidth}
                                            onOrderChange={setSelectedColumnIds}
                                            onWidthsChange={widths => setColumnWidths(current => ({
                                                ...current,
                                                ...widths,
                                            }))}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{display: "block", mt: 0.5, color: "text.secondary"}}
                                        >
                                            {t("Drag columns to reorder, drag edges to resize.")}
                                        </Typography>
                                    </Box>
                                )}

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: {xs: 1, sm: 1.5},
                                        minHeight: {lg: "68vh"},
                                        maxHeight: {xs: "58vh", lg: "68vh"},
                                        overflow: "auto",
                                        bgcolor: "grey.100",
                                    }}
                                >
                                    <PrintPreviewScaler pageWidth={pageSize.width}>
                                        <Box
                                            sx={{
                                                width: pageSize.width,
                                                minHeight: pageSize.height,
                                                p: `${A4_PAGE_PADDING}px`,
                                                boxSizing: "border-box",
                                                bgcolor: "common.white",
                                                color: "text.primary",
                                                boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
                                            }}
                                        >
                                            <div
                                                ref={printRef}
                                                className="legacy-agenda-print-root"
                                                style={{width: "100%"}}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        alignItems: "flex-end",
                                                        flexDirection: "column",
                                                        gap: 12,
                                                        marginBottom: 24,
                                                    }}
                                                >
                                                    <img
                                                        src={headerLogo}
                                                        alt={organization?.name ?? t("Organization logo")}
                                                        style={{maxHeight: 80, maxWidth: 120, objectFit: "contain"}}
                                                    />
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "flex-end",
                                                            textAlign: "right",
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            className="legacy-agenda-last-name"
                                                            sx={{fontSize: fontSize + 8, fontWeight: 700}}
                                                        >
                                                            {lastName}
                                                        </Typography>
                                                        <Typography
                                                            className="legacy-agenda-first-name"
                                                            sx={{fontSize: fontSize + 4}}
                                                        >
                                                            {firstAndPrefix}
                                                        </Typography>
                                                        <Typography sx={{fontSize}}>
                                                            {formatFullDate(printStartDate)} — {formatFullDate(printEndDate)}
                                                        </Typography>
                                                        <Typography sx={{fontSize}}>{activeStay?.roomId ?? "-"}</Typography>
                                                    </div>
                                                </div>

                                                {!hasAgenda ? (
                                                    <Typography
                                                        className="legacy-agenda-empty"
                                                        sx={{
                                                            fontSize: fontSize + 12,
                                                            textAlign: "center",
                                                            mt: 6,
                                                            color: CommonThemeColors.ref.neutral.neutral60,
                                                        }}
                                                    >
                                                        {t("This guest has no agenda")}
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        {renderGridTable(t("Weekly Planner"), agendaRows.weekly)}
                                                        {renderGridTable(t("Spa"), agendaRows.spa)}
                                                        {renderGridTable(t("Restaurant"), agendaRows.restaurant)}
                                                        {renderGridTable(t("Other"), agendaRows.other)}
                                                    </>
                                                )}
                                            </div>
                                        </Box>
                                    </PrintPreviewScaler>
                                </Paper>
                            </Stack>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{px: {xs: 1.5, sm: 3}, pb: 2, pt: 1}}>
                        <Button
                            color="inherit"
                            startIcon={<RestartAltIcon/>}
                            onClick={resetPrintConfig}
                            sx={{mr: "auto"}}
                        >
                            {t("Reset to defaults")}
                        </Button>
                        <Button onClick={() => setIsPreviewOpen(false)}>{t("Cancel")}</Button>
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon/>}
                            disabled={!canPrint}
                            onClick={confirmPrint}
                        >
                            {t("Print")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default LegacyAgendatPrintButton;
